'use client';

import { BannerMedia } from '@payload-types';

import { styles } from './UiBannerPicture.styles';
import { getCustomUrls, getSize } from './utils';

type SizeKey = keyof NonNullable<BannerMedia['sizes']>;

type UiBannerPictureProps = {
  sizes: BannerMedia['sizes'];
  desktopFieldName: SizeKey;
  mobileFieldName: SizeKey;
  customSizes?: BannerMedia['customSizes'];
  alt?: string;
  className?: string;
  classNameImage?: string;
  /** Приоритетная загрузка (LCP первого баннера) */
  priority?: boolean;
};

export function UiBannerPicture({
  sizes,
  desktopFieldName,
  mobileFieldName,
  customSizes,
  alt = '',
  className,
  classNameImage,
  priority = false
}: UiBannerPictureProps) {
  const slots = styles();

  const { desktopCustomUrl, mobileCustomUrl } = getCustomUrls(
    desktopFieldName,
    mobileFieldName,
    customSizes
  );

  const desktop = getSize(sizes, desktopFieldName, desktopCustomUrl);
  const desktop2x = getSize(sizes, `${desktopFieldName}@2x` as SizeKey, desktopCustomUrl);
  const mobile = getSize(sizes, mobileFieldName, mobileCustomUrl);
  const mobile2x = getSize(sizes, `${mobileFieldName}@2x` as SizeKey, mobileCustomUrl);

  const desktopSrcSet = [
    desktop?.url && `${desktop.url} 1x`,
    desktop2x?.url && `${desktop2x.url} 2x`
  ]
    .filter(Boolean)
    .join(', ');

  const mobileSrcSet = [
    mobile?.url && `${mobile.url} 1x`,
    mobile2x?.url && `${mobile2x.url} 2x`
  ]
    .filter(Boolean)
    .join(', ');

  const fallbackUrl =
    mobile?.url || desktop?.url || mobile2x?.url || desktop2x?.url || '';
  const fallbackWidth = mobile?.width || desktop?.width || undefined;
  const fallbackHeight = mobile?.height || desktop?.height || undefined;

  if (!fallbackUrl) {
    return null;
  }

  return (
    <picture className={slots.base({ className })}>
      {desktopSrcSet && (
        <source
          media="(min-width: 768px)"
          srcSet={desktopSrcSet}
        />
      )}
      {mobileSrcSet && (
        <source
          media="(max-width: 767px)"
          srcSet={mobileSrcSet}
        />
      )}
      <img
        src={fallbackUrl}
        alt={alt}
        width={fallbackWidth}
        height={fallbackHeight}
        className={slots.image({ className: classNameImage })}
        fetchPriority={priority ? 'high' : undefined}
        loading={priority ? 'eager' : undefined}
      />
    </picture>
  );
}
